import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { ApiService } from '../../../core/services/api.service';
import { PageTitleService } from '../../../core/services/page-title.service';
import { VioTablesV2Module, VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { CATEGORY_TABLE_BASE } from '../table-configs';
import { autoEnglishName } from '../../../core/utils/transliterate.util';
import { DateFormatService } from '../../../core/services/date-format.service';

@Component({
  selector: 'app-category-master',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, VioTablesV2Module, FormsModule],
  templateUrl: './category-master.component.html',
  styleUrl: './category-master.component.scss',
})
export class CategoryMasterComponent implements OnInit, OnDestroy {
  view: 'list' | 'form' = 'list';

  // List state
  records: any[] = [];
  isListLoading = false;

  // Form state
  form!: FormGroup;
  isEditMode = false;
  editId: string | null = null;
  isSaving = false;
  successMessage = '';
  errorMessage = '';

  // Delete state
  showDeleteConfirm: string | null = null;
  isDeleting = false;
  deleteErrorMessage = '';

  // Category books modal
  showCategoryBooksModal = false;
  selectedCategoryForBooks: any = null;
  categoryBooks: any[] = [];
  isLoadingCategoryBooks = false;
  categoryBooksError = '';

  // Copies modal (stacks over category books modal)
  showCopiesModal = false;
  selectedCopiesBook: any = null;

  // Suggestion state
  nameSuggestions: any[] = [];
  englishNameSuggestions: any[] = [];

  private destroy$ = new Subject<void>();

  tableConfig: VT2_TABLE_CONFIG = {
    ...CATEGORY_TABLE_BASE,
    actionButtons: [
      { icon: '✏', title: 'Edit', cssClass: 'vt2-icon-btn--edit', onClick: (row) => this.editRecord(row) },
      { icon: '📚', title: 'Books & Locations', cssClass: 'vt2-icon-btn--view', onClick: (row) => this.openCategoryBooks(row) },
    ],
  };

  constructor(private fb: FormBuilder, private api: ApiService, private pageTitle: PageTitleService, private dateFmt: DateFormatService) { }

  ngOnInit(): void {
    this.tableConfig = { ...this.tableConfig, dateDisplayFormat: this.dateFmt.current };
    this.pageTitle.set('Category Master', 'Manage book categories');
    this.buildForm();
    this.loadList();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  buildForm(): void {
    this.form = this.fb.group({
      categoryName:        ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      categoryNameEnglish: ['', [Validators.maxLength(150)]],
    });

    this.form.get('categoryName')!.valueChanges.pipe(
      debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$)
    ).subscribe(val => {
      if (val && val.trim().length >= 2) {
        this.api.getCategories({ search: val.trim(), field: 'categoryName' }).subscribe({
          next: (res) => { this.nameSuggestions = (res || []).slice(0, 5); },
          error: () => { this.nameSuggestions = []; }
        });
      } else { this.nameSuggestions = []; }
    });

    this.form.get('categoryNameEnglish')!.valueChanges.pipe(
      debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$)
    ).subscribe(val => {
      if (val && val.trim().length >= 2) {
        this.api.getCategories({ search: val.trim(), field: 'categoryNameEnglish' }).subscribe({
          next: (res) => { this.englishNameSuggestions = (res || []).slice(0, 5); },
          error: () => { this.englishNameSuggestions = []; }
        });
      } else { this.englishNameSuggestions = []; }
    });
  }

  loadList(): void {
    this.isListLoading = true;
    this.api.getCategories().subscribe({
      next: (data) => {
        this.records = data || [];
        this.isListLoading = false;
      },
      error: () => {
        this.records = [];
        this.isListLoading = false;
      },
    });
  }

  addNew(): void {
    this.isEditMode = false;
    this.editId = null;
    this.form.reset({}, { emitEvent: false });
    this.nameSuggestions = [];
    this.englishNameSuggestions = [];
    this.successMessage = '';
    this.errorMessage = '';
    this.view = 'form';
    this.pageTitle.set('Category Master', 'Add a new book category');
  }

  editRecord(record: any): void {
    this.isEditMode = true;
    this.editId = record._id || record.id;
    this.nameSuggestions = [];
    this.englishNameSuggestions = [];
    this.successMessage = '';
    this.errorMessage = '';
    this.form.patchValue({
      categoryName:        record.categoryName,
      categoryNameEnglish: record.categoryNameEnglish || '',
    }, { emitEvent: false });
    this.view = 'form';
    this.pageTitle.set('Category Master', 'Update category details');
  }

  backToList(): void {
    this.nameSuggestions = [];
    this.englishNameSuggestions = [];
    this.view = 'list';
    this.pageTitle.set('Category Master', 'Manage book categories');
    this.loadList();
  }

  get f() { return this.form.controls; }

  isFieldInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && ctrl.touched);
  }

  onCategoryNameBlur(): void {
    const name = (this.form.get('categoryName')!.value || '').trim();
    if (!name) return;
    const transliterated = autoEnglishName(name);
    if (transliterated) {
      this.form.get('categoryNameEnglish')!.setValue(transliterated);
    } else if (/^[a-zA-Z\s'.\-,]+$/.test(name)) {
      this.form.get('categoryNameEnglish')!.setValue(name);
    }
    // emitEvent: true (default) → triggers debounced search on categoryNameEnglish
  }

  dismissNameSuggestions(): void { this.nameSuggestions = []; }
  dismissEnglishNameSuggestions(): void { this.englishNameSuggestions = []; }

  confirmDelete(id: string): void {
    this.showDeleteConfirm = id;
    this.deleteErrorMessage = '';
  }

  cancelDelete(): void {
    this.showDeleteConfirm = null;
    this.deleteErrorMessage = '';
  }

  executeDelete(id: string): void {
    this.isDeleting = true;
    this.deleteErrorMessage = '';
    this.api.deleteCategory(id).subscribe({
      next: () => {
        this.isDeleting = false;
        this.showDeleteConfirm = null;
        this.loadList();
      },
      error: (err: any) => {
        this.isDeleting = false;
        this.deleteErrorMessage = err?.message || 'Failed to delete category.';
      },
    });
  }

  openCategoryBooks(category: any): void {
    this.selectedCategoryForBooks = category;
    this.categoryBooks = [];
    this.categoryBooksError = '';
    this.showCopiesModal = false;
    this.isLoadingCategoryBooks = true;
    this.showCategoryBooksModal = true;
    this.api.locateBooksByCategory(category.categoryName).subscribe({
      next: (books) => { this.categoryBooks = books || []; this.isLoadingCategoryBooks = false; },
      error: (err: any) => { this.categoryBooksError = err?.message || 'Failed to load books.'; this.isLoadingCategoryBooks = false; },
    });
  }

  closeCategoryBooksModal(): void {
    this.showCategoryBooksModal = false;
    this.showCopiesModal = false;
    this.selectedCategoryForBooks = null;
  }

  openCopiesModal(book: any): void {
    this.selectedCopiesBook = book;
    this.showCopiesModal = true;
  }

  closeCopiesModal(): void {
    this.showCopiesModal = false;
    this.selectedCopiesBook = null;
  }

  catBookStatusLabel(status: string): string {
    if (status === 'C') return 'At Center';
    if (status === 'M') return 'Issued';
    if (status === 'S') return 'Scrapped';
    return status;
  }

  catBookRowClass(status: string): string {
    if (status === 'C') return 'cb-row-c';
    if (status === 'M') return 'cb-row-m';
    if (status === 'S') return 'cb-row-s';
    return '';
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSaving = true;
    this.successMessage = '';
    this.errorMessage = '';

    const request$ = this.isEditMode
      ? this.api.updateCategory({ ...this.form.value, id: this.editId })
      : this.api.saveCategory(this.form.value);

    request$.subscribe({
      next: () => {
        this.isSaving = false;
        this.successMessage = this.isEditMode ? 'Category updated successfully!' : 'Category saved successfully!';
        setTimeout(() => this.backToList(), 1000);
      },
      error: (err: any) => {
        this.isSaving = false;
        this.errorMessage = err?.error?.message || 'Failed to save category.';
      },
    });
  }


  onEdit(row: any) {
    console.log('Edit clicked:', row);
  }

  onDelete(row: any) {
    this.records = this.records.filter(m => m.id !== row.id);
    console.log('Deleted member id:', row.id);
  }

  onRowClick(row: any) {
    console.log('Row clicked:', row);
  }

  onCellEdit(event: { row: any; field: string; oldValue: any; newValue: any }) {
    console.log('Cell edited:', event);
  }

  onRestoreRow(row: any) {
    this.records = [...this.records, row];
    console.log('Row restored:', row);
  }
}
